export { BROWSER_ENGINE_V2_FLAG, isBrowserEngineV2Enabled } from './GameShellFeatureFlag';
/**
 * Root-level owner of the confirmed game view.
 *
 * The first increment is deliberately small:
 * - it stores only a server-authorized PlayerViewModel;
 * - intents are queued and forwarded, never folded into the confirmed view
 *   (no prediction/optimistic state);
 * - the singleton survives PlayerHome/WaitingFor remounts;
 * - everything stays in memory: no localStorage, IndexedDB, BroadcastChannel
 *   or cookies are used for the view or cards.
 */
export class GameShellService {
    constructor() {
        this.status = 'stopped';
        this.confirmedView = undefined;
        this.viewRevision = 0;
        this.pending = new Map();
        this.listeners = new Set();
        this.workerPort = undefined;
        this.unsubscribeWorker = undefined;
    }
    getStatus() {
        return this.status;
    }
    isRunning() {
        return this.status === 'running';
    }
    /**
     * Starts the lifecycle. Idempotent while running. An optional Worker port
     * is attached/replaced for the future Worker adapter.
     */
    start(workerPort) {
        const wasRunning = this.status === 'running';
        this.status = 'running';
        let changed = !wasRunning;
        if (workerPort !== undefined && workerPort !== this.workerPort) {
            this.detachWorker();
            this.workerPort = workerPort;
            this.unsubscribeWorker = workerPort.subscribe((message) => this.handleWorkerMessage(message));
            workerPort.start();
            changed = true;
        }
        if (changed) {
            this.emit();
        }
    }
    /**
     * Stops the lifecycle, detaches the Worker port and drops pending intents.
     * The last confirmed view is retained (memory-only) so a remount can still
     * render the authorized snapshot without prediction.
     */
    stop() {
        if (this.status === 'stopped' && this.workerPort === undefined) {
            return;
        }
        this.detachWorker();
        this.pending.clear();
        this.status = 'stopped';
        this.emit();
    }
    /**
     * Applies a server-confirmed, authorized view. This is the only writer of
     * confirmedView and viewRevision. When revision is omitted the local
     * revision advances by one.
     */
    setConfirmedView(view, revision) {
        const nextRevision = revision === undefined ? this.viewRevision + 1 : revision;
        if (!Number.isSafeInteger(nextRevision) || nextRevision < 0 || nextRevision < this.viewRevision) {
            throw new Error('viewRevision must be a monotonic non-negative integer');
        }
        this.confirmedView = view;
        this.viewRevision = nextRevision;
        this.emit();
    }
    /** Explicit alias for callers that prefer the confirmed-view vocabulary. */
    confirmView(view, revision) {
        this.setConfirmedView(view, revision);
    }
    getConfirmedView() {
        return this.confirmedView;
    }
    getViewRevision() {
        return this.viewRevision;
    }
    addPendingIntent(intent) {
        if (this.pending.has(intent.actionId)) {
            return;
        }
        this.pending.set(intent.actionId, { ...intent });
        this.workerPort?.send({ ...intent });
        this.emit();
    }
    removePendingIntent(actionId) {
        if (this.pending.delete(actionId)) {
            this.emit();
        }
    }
    getPendingIntents() {
        return Array.from(this.pending.values(), (intent) => ({ ...intent }));
    }
    clearPendingIntents() {
        if (this.pending.size === 0) {
            return;
        }
        this.pending.clear();
        this.emit();
    }
    /** Used on logout or a full authorized resync with a different player. */
    reset() {
        this.confirmedView = undefined;
        this.viewRevision = 0;
        this.pending.clear();
        this.emit();
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    snapshot() {
        return {
            status: this.status,
            confirmedView: this.confirmedView,
            viewRevision: this.viewRevision,
            pendingIntents: this.getPendingIntents(),
        };
    }
    getSnapshot() {
        return this.snapshot();
    }
    handleWorkerMessage(message) {
        switch (message.type) {
            case 'confirmed-view':
                if (message.viewRevision !== undefined && message.viewRevision < this.viewRevision) {
                    return;
                }
                if (message.actionId !== undefined) {
                    this.pending.delete(message.actionId);
                }
                this.setConfirmedView(message.playerView, message.viewRevision);
                break;
            case 'intent-rejected':
                this.removePendingIntent(message.actionId);
                break;
        }
    }
    detachWorker() {
        this.unsubscribeWorker?.();
        this.unsubscribeWorker = undefined;
        if (this.workerPort !== undefined) {
            this.workerPort.stop();
            this.workerPort = undefined;
        }
    }
    emit() {
        const snapshot = this.snapshot();
        for (const listener of Array.from(this.listeners)) {
            listener(snapshot);
        }
    }
}
/** One instance for the browser root, independent of component remounts. */
export const gameShell = new GameShellService();
